import { useEffect, useId, useState } from "react";
import { businessApi } from "../../../../services/api";
import { Buttons, FormError, ModalFrame, inputClass, labelClass } from "./MyHrParts";
import { errorText } from "../myHrText";

/**
 * Ask for leave (HR revamp Batch 4). The server counts the working days from the employee's schedule;
 * the modal shows that count before sending. Every request waits for someone else to review it (APR-5).
 */
export function LeaveRequestModal({ balances = [], types = [], onClose, onSubmitted }) {
  const [leaveTypeId, setLeaveTypeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const errorId = useId();

  const lastDay = endDate || startDate;
  useEffect(() => {
    setPreview(null);
    setPreviewError("");
    if (!startDate || !lastDay || lastDay < startDate) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      businessApi.previewMyLeave({ start_date: startDate, end_date: lastDay })
        .then((res) => { if (active) setPreview(res?.preview || null); })
        .catch((err) => { if (active) setPreviewError(errorText(err, "Could not check those dates.")); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [startDate, lastDay]);

  const balance = balances.find((row) => String(row.leaveTypeId) === String(leaveTypeId));
  const chosenType = types.find((type) => String(type.id) === String(leaveTypeId));
  // Unpaid leave uses no paid entitlement, so it needs no balance (APP-HR-019); it is still reviewed.
  const unpaid = chosenType ? chosenType.isPaid === false : false;
  const trimmed = reason.trim();
  const overBalance = Boolean(preview && leaveTypeId && !unpaid && (!balance || preview.total_days > balance.available));
  const valid = Boolean(leaveTypeId && startDate && lastDay >= startDate && preview && !overBalance && trimmed.length >= 5);

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      await businessApi.submitMyLeave({ leave_type_id: Number(leaveTypeId), start_date: startDate, end_date: lastDay, reason: trimmed });
      onSubmitted?.();
    } catch (err) {
      setError(errorText(err, "Could not send the leave request. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalFrame
      title="Request leave"
      subtitle="Someone who reviews leave decides it. Your balance is used only if it is approved."
      onClose={onClose}
      footer={<Buttons onClose={onClose} saving={saving} disabled={!valid} label="Send for review" />}
    >
      <form id="my-hr-request-form" onSubmit={submit} className="space-y-4" aria-describedby={error ? errorId : undefined}>
        <div>
          <label htmlFor="leave-type" className={labelClass}>Leave type *</label>
          <select id="leave-type" required value={leaveTypeId} onChange={(e) => setLeaveTypeId(e.target.value)} className={inputClass}>
            <option value="">Choose a leave type</option>
            {types.map((type) => {
              const row = balances.find((item) => item.leaveTypeId === type.id);
              const left = type.isPaid === false
                ? "unpaid, no balance needed"
                : (row ? `${row.available} day${row.available === 1 ? "" : "s"} left` : "no balance set");
              return <option key={type.id} value={type.id}>{`${type.name} · ${left}`}</option>;
            })}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="leave-from" className={labelClass}>First day *</label>
            <input id="leave-from" type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="leave-to" className={labelClass}>Last day</label>
            <input id="leave-to" type="date" min={startDate || undefined} value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputClass} />
          </div>
        </div>
        <div role="status" className="rounded-panel bg-surface-sunken p-3 text-body-sm">
          {previewError ? <span className="text-danger">{previewError}</span>
            : preview ? (
              <>
                <span className="font-semibold">{preview.total_days} working day{preview.total_days === 1 ? "" : "s"}</span>
                <span className="text-secondary"> from your schedule. Rest days, official holidays and closure days are not counted.</span>
                {unpaid ? <span className="mt-1 block text-secondary">Unpaid leave: these days are not paid, and no leave balance is used.</span> : null}
                {overBalance ? (
                  <span className="mt-1 block text-danger">
                    {balance ? `That is more than the ${balance.available} day${balance.available === 1 ? "" : "s"} you have left.` : "You have no balance for this leave type. Ask HR to set it up."}
                  </span>
                ) : null}
              </>
            ) : <span className="text-secondary">Pick the dates to see how many working days this uses.</span>}
        </div>
        <div>
          <label htmlFor="leave-reason" className={labelClass}>Reason *</label>
          <textarea id="leave-reason" rows={3} maxLength={1000} required value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} placeholder="For example: family trip to the province." />
          <p className="mt-1 text-meta text-tertiary">At least 5 characters. Your reviewer sees this.</p>
        </div>
        <FormError id={errorId} message={error} />
      </form>
    </ModalFrame>
  );
}

/**
 * Overtime: an employee asks for it (My HR), or a manager assigns it to someone (HR → Attendance).
 * Overtime is time before or after the shift; only approved overtime is paid.
 */
export function OvertimeModal({ mode = "request", employees = [], initial = {}, canApprove = false, onClose, onSubmitted }) {
  const [memberId, setMemberId] = useState(initial.memberId ? String(initial.memberId) : "");
  const [date, setDate] = useState(initial.date || "");
  const [startTime, setStartTime] = useState(initial.startTime || "");
  const [endTime, setEndTime] = useState(initial.endTime || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const errorId = useId();
  const assigning = mode === "assign";

  const trimmed = reason.trim();
  const valid = Boolean((!assigning || memberId) && date && startTime && endTime && startTime !== endTime && trimmed.length >= 5);
  const nextMorning = Boolean(startTime && endTime && endTime < startTime);

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    const payload = { date, start_time: startTime, end_time: endTime, reason: trimmed };
    try {
      const res = assigning
        ? await businessApi.assignOvertime({ ...payload, business_member_id: Number(memberId) })
        : await businessApi.requestOvertime(payload);
      onSubmitted?.(res?.request || null);
    } catch (err) {
      setError(errorText(err, "Could not save the overtime. Try again."));
    } finally {
      setSaving(false);
    }
  };

  let subtitle = "Time after your shift is recorded, but only approved overtime is paid. Someone else reviews this.";
  if (assigning) {
    subtitle = canApprove
      ? "You can approve overtime, so this is approved when you save it."
      : "You can't approve overtime, so this waits for someone who can.";
  }
  let label = "Send for review";
  if (assigning) label = canApprove ? "Assign and approve" : "Assign for approval";

  return (
    <ModalFrame
      title={assigning ? "Assign overtime" : "Request overtime"}
      subtitle={subtitle}
      onClose={onClose}
      footer={<Buttons onClose={onClose} saving={saving} disabled={!valid} label={label} />}
    >
      <form id="my-hr-request-form" onSubmit={submit} className="space-y-4" aria-describedby={error ? errorId : undefined}>
        {assigning ? (
          <div>
            <label htmlFor="ot-member" className={labelClass}>Employee *</label>
            <select id="ot-member" required value={memberId} onChange={(e) => setMemberId(e.target.value)} className={inputClass}>
              <option value="">Choose an employee</option>
              {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
            </select>
          </div>
        ) : null}
        <div>
          <label htmlFor="ot-date" className={labelClass}>Work day *</label>
          <input id="ot-date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
          <p className="mt-1 text-meta text-tertiary">The day of the shift this overtime belongs to (up to 14 days back, 60 days ahead). The 14 days is this app's request limit; older time that was worked is not lost: HR checks it.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="ot-start" className={labelClass}>From *</label>
            <input id="ot-start" type="time" required value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="ot-end" className={labelClass}>Until *</label>
            <input id="ot-end" type="time" required value={endTime} onChange={(e) => setEndTime(e.target.value)} className={inputClass} />
          </div>
        </div>
        <p className="-mt-2 text-meta text-tertiary">{nextMorning ? "Ends the next morning. " : ""}Overtime is time before or after the shift, not during it.</p>
        <div>
          <label htmlFor="ot-reason" className={labelClass}>What is it for? *</label>
          <textarea id="ot-reason" rows={3} maxLength={500} required value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} placeholder="For example: inventory count after closing." />
          <p className="mt-1 text-meta text-tertiary">At least 5 characters.</p>
        </div>
        <FormError id={errorId} message={error} />
      </form>
    </ModalFrame>
  );
}
